import axios from 'axios';
import { StrapiResponse, HomePage, BibliotecaPage } from '../types/strapi';
import { STRAPI_API_URL } from '../config/strapi';

// Configuração base do axios
const strapiApi = axios.create({
  baseURL: STRAPI_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

export const strapiService = {
  /**
   * Busca a página home do Strapi
   */
  async getHomePage(): Promise<StrapiResponse<HomePage>> {
    try {
      const response = await strapiApi.get('/home-page', {
        params: {
          populate: {
            hero: {
              populate: {
                backgroundImage: true,
              },
            },
            sections: {
              populate: '*',
            },
            content: {
              populate: '*',
            },
          },
        },
      });
      return response.data as StrapiResponse<HomePage>;
    } catch (error) {
      console.error('Erro ao buscar página home:', error);
      throw new Error('Falha ao carregar dados da página home');
    }
  },

  /**
   * Busca componentes dinâmicos do Strapi
   */
  async getComponents(): Promise<StrapiResponse<any[]>> {
    try {
      const response = await strapiApi.get('/components', {
        params: {
          populate: '*',
        },
      });
      return response.data as StrapiResponse<any[]>;
    } catch (error) {
      console.error('Erro ao buscar componentes:', error);
      throw new Error('Falha ao carregar componentes');
    }
  },

  /**
   * Busca uma página específica por slug
   */
  async getPageBySlug(slug: string): Promise<StrapiResponse<any>> {
    try {
      const response = await strapiApi.get(`/pages`, {
        params: {
          filters: {
            slug: {
              $eq: slug,
            },
          },
          populate: '*',
        },
      });
      return response.data as StrapiResponse<any>;
    } catch (error) {
      console.error(`Erro ao buscar página ${slug}:`, error);
      throw new Error(`Falha ao carregar página ${slug}`);
    }
  },

  /**
   * Busca o card da first-section do Strapi
   */
  async getFirstSection(): Promise<StrapiResponse<any>> {
    try {
      const response = await strapiApi.get('/first-section', {
        params: {
          populate: {
            Card: {
              populate: {
                Header: true,
                Button: true,
                background: true,
                Cont: true,
              },
            },
          },
        },
      });
      return response.data as StrapiResponse<any>;
    } catch (error) {
      console.error('Erro ao buscar first-section:', error);
      throw new Error('Falha ao carregar dados da first-section');
    }
  },

  /**
   * Busca dados globais (navbar) do Strapi
   */
  async getGlobal(): Promise<StrapiResponse<any>> {
    try {
      const response = await strapiApi.get('/global', {
        params: {
          populate: {
            Navbar: {
              populate: {
                items: true,
                intranet: true,
              },
            },
            ImageBar: {
              populate: '*'
            },
            Footer: {
              populate: {
                Logo: { populate: '*' },
                ImageLink: { populate: '*' },
                Adress: true,
                Button: true,
              },
            },
          },
        },
      });
      return response.data as StrapiResponse<any>;
    } catch (error) {
      console.error('Erro ao buscar dados globais:', error);
      throw new Error('Falha ao carregar dados globais');
    }
  },

  /**
   * Busca o componente completo da tela home (dynamic_zone)
   */
  async getHome(): Promise<StrapiResponse<any>> {
    try {
      const response = await strapiApi.get('/home', {
        params: {
          populate: {
            dynamic_zone: {
              on: {
                'cards.first-card': {
                  populate: {
                    Header: true,
                    Button: true,
                    background: true,
                    Cont: true,
                  },
                },
                'cards.second-card': {
                  populate: {
                    Header: true,
                    image: true,
                  },
                },
                'cards.third-card': {
                  populate: {
                    Button: true,
                  },
                },
                'cards.fourth-card': {
                  populate: {
                    list: true,
                    image: true,
                  },
                },
              },
            },
          },
        },
      });
      return response.data as StrapiResponse<any>;
    } catch (error) {
      console.error('Erro ao buscar home:', error);
      throw new Error('Falha ao carregar dados da home');
    }
  },

  /**
   * Busca dados da página de notícias do Strapi
   */
  async getNoticias(): Promise<StrapiResponse<any>> {
    try {
      const response = await strapiApi.get('/noticia', {
        params: {
          populate: {
            BuscarNoticia: {
              populate: '*'
            },
            list_noticias: {
              populate: {
                image: true,
                header: true,
                noticiaPage: {
                  populate: {
                    mainImage: true,
                    secImage: true
                  }
                }
              },
            },
          },
        },
      });
      return response.data as StrapiResponse<any>;
    } catch (error) {
      console.error('Erro ao buscar notícias:', error);
      throw new Error('Falha ao carregar dados das notícias');
    }
  },

  /**
   * Busca dados da página de dúvidas frequentes do Strapi
   */
  async getFaq(): Promise<StrapiResponse<any>> {
    try {
      const response = await strapiApi.get('/faq', {
        params: {
          populate: {
            FAQCards: {
              populate: {
                DuvidasFrequentes: {
                  populate: '*'
                }
              }
            }
          },
        },
      });
      return response.data as StrapiResponse<any>;
    } catch (error) {
      console.error('Erro ao buscar FAQs:', error);
      throw new Error('Falha ao carregar dados das dúvidas frequentes');
    }
  },

  /**
   * Busca dados da página de biblioteca do Strapi
   */
  async getBiblioteca(): Promise<StrapiResponse<BibliotecaPage>> {
    try {
      const response = await strapiApi.get('/biblioteca', {
        params: {
          populate: {
            ArquivosList: {
              populate: {
                File: {
                  populate: '*'
                }
              }
            }
          },
        },
      });
      return response.data as StrapiResponse<BibliotecaPage>;
    } catch (error) {
      console.error('Erro ao buscar biblioteca:', error);
      throw new Error('Falha ao carregar dados da biblioteca');
    }
  },

  /**
   * Busca dados da página de legislação do Strapi
   */
  async getLegislacao(): Promise<StrapiResponse<any>> {
    try {
      const response = await strapiApi.get('/legislacao', {
        params: {
          populate: {
            legislacaoList: {
              populate: {
                File: {
                  populate: {
                    arquivo: true
                  }
                }
              }
            }
          },
        },
      });
      return response.data as StrapiResponse<any>;
    } catch (error) {
      console.error('Erro ao buscar legislação:', error);
      throw new Error('Falha ao carregar dados da legislação');
    }
  },

  /**
   * Busca dados da página de documentos do Strapi
   */
  async getDocumento(): Promise<StrapiResponse<any>> {
    try {
      const response = await strapiApi.get('/documento', {
        params: {
          populate: {
            documentoList: {
              populate: {
                File: {
                  populate: {
                    arquivo: true
                  }
                }
              }
            }
          },
        },
      });
      return response.data as StrapiResponse<any>;
    } catch (error) {
      console.error('Erro ao buscar documentos:', error);
      throw new Error('Falha ao carregar dados dos documentos');
    }
  },
}; 